import React, { useState, useEffect, useMemo, useContext, createContext } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, AlertTriangle, ExternalLink, Search, X } from "lucide-react";
import Layout from "../components/Layout";
import { Pill, PillKind } from "../components/ui/pill";
import { Button } from "../components/ui/button";
import { FilterDropdown } from "../components/ui/filter-dropdown";
import { DataTable, DataTableColumn } from "../components/ui/data-table";
import { SectionLabel } from "../components/ui/section-label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { useQueryClient } from "@tanstack/react-query";
import { usePartners, useSyncSheetsLogs, useSyncOverview, useSyncIssues } from "../hooks/queries";
import {
  SyncIssue, SyncPartnerOverview, SyncRunStatus, SyncRunTrigger, SyncSheetResult, SyncSheetsLog, SyncVacatureRef,
  setPartnerSheetsSyncEnabled,
} from "../api/api";
import { toast } from "../lib/toast";
import { cn } from "@/lib/utils";

const LIMIT = 25;
const ISSUE_DAYS = 7;
const ISSUES_COLLAPSED = 8;

const STATUS_PILL: Record<SyncRunStatus, PillKind> = {
  success: "health-ok",
  warning: "hold",
  error: "health-error",
  running: "health-unconfigured",
};

const STATUS_LABEL: Record<SyncRunStatus, string> = {
  success: "Geslaagd",
  warning: "Waarschuwing",
  error: "Fout",
  running: "Bezig",
};

const TRIGGER_LABEL: Record<SyncRunTrigger, string> = {
  scheduled_pull: "Automatisch ophalen",
  edit_push: "Wijziging in sheet",
  manual: "Handmatig",
  "": "Onbekend",
};

const STATUS_FILTER = [
  { value: "alle", label: "Alle statussen" },
  { value: "success", label: "Geslaagd" },
  { value: "warning", label: "Waarschuwing" },
  { value: "error", label: "Fout" },
  { value: "running", label: "Bezig" },
];

const TRIGGER_FILTER = [
  { value: "alle", label: "Alle aanleidingen" },
  { value: "edit_push", label: "Wijziging in sheet" },
  { value: "scheduled_pull", label: "Automatisch ophalen" },
  { value: "manual", label: "Handmatig" },
];

const ISSUE_PILL: Record<SyncIssue["kind"], [PillKind, string]> = {
  error: ["health-error", "Fout"],
  warning: ["hold", "Waarschuwing"],
  skipped: ["health-unconfigured", "Overgeslagen"],
};

// Sheet-owned job opening columns, as reported in push_result.updated[].fields.
const FIELD_LABEL: Record<string, string> = {
  role: "functie",
  priority: "prioriteit",
  experience: "ervaring",
  disciplines: "disciplines",
  location: "locatie",
  number_of_positions: "aantal posities",
  status: "status",
  hiring_manager: "hiring manager",
  comments: "opmerkingen",
  branch_id: "vestiging",
};

// Backend skip reasons (internal/service/sync.go) in Dutch.
const SKIP_REASON: Record<string, string> = {
  "empty role": "geen functie ingevuld",
  "tab name does not match a branch": "tabbladnaam is geen vestiging",
  "job opening belongs to another partner": "deze vacature hoort bij een andere partner",
  "job opening was deleted in the dashboard": "deze vacature is in het dashboard verwijderd",
};

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

// formatRelative renders "zojuist", "12 min geleden", "3 uur geleden", or a date.
function formatRelative(iso: string | null): string {
  if (!iso) return "nooit";
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "zojuist";
  if (minutes < 60) return `${minutes} min geleden`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} uur geleden`;
  return new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(log: SyncSheetsLog): string | null {
  if (!log.started_at || !log.finished_at) return null;
  const seconds = (new Date(log.finished_at).getTime() - new Date(log.started_at).getTime()) / 1000;
  return `${seconds.toLocaleString("nl-NL", { maximumFractionDigits: 1 })} s`;
}

// rowLabel names a sheet row by its number in the tab. Logs from older scripts only
// carry the row's position in the push (row_index).
function rowLabel(r: { sheet_row?: number; row_index: number }): string {
  return r.sheet_row ? `Rij ${r.sheet_row}` : `Verzonden rij ${r.row_index + 1}`;
}

// allErrors collects the script's errors and the backend's push/delete errors.
function allErrors(log: SyncSheetsLog): string[] {
  const errors = [...(log.errors ?? [])];
  if (log.push_result?.error) errors.push(`Push teruggedraaid: ${log.push_result.error}`);
  for (const e of log.push_result?.errors ?? []) {
    errors.push(`${rowLabel(e)} (${e.tab_name}${e.id ? `, #${e.id}` : ""}): ${e.error}`);
  }
  if (log.delete_result?.error) errors.push(`Verwijderen teruggedraaid: ${log.delete_result.error}`);
  return errors;
}

// ── Links: vacatures and sheet rows ────────────────────────────────────────────

// The job openings the current page refers to, so IDs can show their title.
const VacatureRefs = createContext<Record<string, SyncVacatureRef>>({});

// SheetRowLink builds a link to a tab and row of the spreadsheet a log came
// from, or null if the log has no (safe) spreadsheet URL or doesn't know the tab.
type SheetRowLink = (tab: string, row?: number) => string | null;
const SheetLinks = createContext<SheetRowLink>(() => null);

function sheetRowLinker(sheet: SyncSheetResult | null | undefined): SheetRowLink {
  const url = sheet?.spreadsheet_url;
  // Same check as the backend: only ever link to Google Sheets.
  if (!url || !url.startsWith("https://docs.google.com/spreadsheets/")) return () => null;
  const base = url.split("#")[0];
  return (tab, row) => {
    const gid = sheet?.tab_gids?.[tab];
    if (gid === undefined) return null;
    return `${base}#gid=${gid}${row ? `&range=A${row}` : ""}`;
  };
}

function VacatureId({ id, deleted }: { id: number; deleted?: boolean }) {
  const ref = useContext(VacatureRefs)[id];
  const isDeleted = deleted ?? ref?.deleted ?? false;
  const title = [
    ref?.role,
    ref?.branch_name && `vestiging ${ref.branch_name}`,
    isDeleted && "verwijderd — de pagina bestaat waarschijnlijk niet meer",
  ].filter(Boolean).join(" · ");
  return (
    <Link to={`/vacature/${id}`} title={title || undefined} className="group inline">
      <span className={cn(
        "font-mono tabular-nums underline decoration-waves-line underline-offset-2 group-hover:decoration-waves-ink",
        isDeleted && "line-through",
      )}>
        #{id}
      </span>
      {ref?.role && <span className="ml-1 text-waves-muted group-hover:text-waves-ink">{ref.role}</span>}
    </Link>
  );
}

function IdList({ ids, deleted }: { ids: number[]; deleted?: boolean }) {
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {ids.map(id => <VacatureId key={id} id={id} deleted={deleted} />)}
    </span>
  );
}

// ID references in free text: "#123", "ID 267", "id=12".
const ID_REFERENCE = /(#|\bID\s+|\bid=)(\d+)/gi;

// IdText renders free text (errors, warnings, reasons) with every database
// ID reference turned into a link to the vacature.
function IdText({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(ID_REFERENCE)) {
    const start = m.index ?? 0;
    parts.push(text.slice(last, start));
    if (m[1] !== "#") parts.push(m[1]);
    parts.push(<VacatureId key={start} id={Number(m[2])} />);
    last = start + m[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

// RowRef shows "Rij 12 (Almere)"; pair it with SheetLink to open that row.
function RowRef({ label, tab }: { label: string; tab: string }) {
  return <>{label} <span className="text-waves-muted">({tab})</span></>;
}

function SheetLink({ tab, row }: { tab: string; row?: number }) {
  const href = useContext(SheetLinks)(tab, row);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Google Sheets"
      className="ml-1 inline-flex align-middle text-waves-muted hover:text-waves-ink"
    >
      <ExternalLink size={13} />
    </a>
  );
}

// ── Detail dialog ──────────────────────────────────────────────────────────────

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{title}</SectionLabel>
      <div className="flex flex-col gap-2 rounded-card border border-waves-line bg-white p-4 text-sm text-waves-body">{children}</div>
    </section>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[200px_1fr]">
      <div className="text-waves-muted">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SyncRunDetail({ log, partnerName }: { log: SyncSheetsLog; partnerName: string }) {
  const errors = allErrors(log);
  const warnings = log.warnings ?? [];
  const push = log.push_result;
  const del = log.delete_result;
  const sheet = log.sheet_result;
  const duration = formatDuration(log);

  const hasDashboardChanges = !!push || !!del;
  // [label, IDs, whether those vacatures no longer exist]
  const sheetLists: [string, number[] | null | undefined, boolean][] = [
    ["Bijgewerkt vanuit dashboard", sheet?.updated_ids, false],
    ["Toegevoegd (nieuw in dashboard)", sheet?.appended_ids, false],
    ["Gemarkeerd als verwijderd", sheet?.marked_deleted_ids, true],
    ["ID gewist (opnieuw invoegen)", sheet?.stale_cleared_ids, true],
    ["Verwijderingen in de wacht", sheet?.held_deletion_ids, false],
  ];

  return (
    <SheetLinks.Provider value={sheetRowLinker(sheet)}>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-3">
          {partnerName}
          <Pill kind={STATUS_PILL[log.status]}>{STATUS_LABEL[log.status]}</Pill>
        </DialogTitle>
        <DialogDescription>
          {TRIGGER_LABEL[log.trigger]} · {formatTimestamp(log.started_at ?? log.created_at)}
          {duration && <> · {duration}</>}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-5">
        {(push?.rolled_back || del?.rolled_back) && (
          <div className="flex items-start gap-3 rounded-card bg-status-alert-bg p-4 text-sm text-status-alert-fg">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              {push?.rolled_back && "De push is volledig teruggedraaid — de genoemde dashboardwijzigingen zijn niet opgeslagen. "}
              {del?.rolled_back && "Het verwijderen is teruggedraaid — er is niets verwijderd."}
            </span>
          </div>
        )}

        {errors.length > 0 && (
          <DetailSection title={`Fouten (${errors.length})`}>
            <ul className="flex flex-col gap-1.5">
              {errors.map((e, i) => <li key={i} className="break-words font-mono text-[13px] text-status-alert-fg"><IdText text={e} /></li>)}
            </ul>
          </DetailSection>
        )}

        {warnings.length > 0 && (
          <DetailSection title={`Waarschuwingen (${warnings.length})`}>
            <ul className="flex flex-col gap-1.5">
              {warnings.map((w, i) => <li key={i} className="break-words font-mono text-[13px]"><IdText text={w} /></li>)}
            </ul>
          </DetailSection>
        )}

        {hasDashboardChanges && (
          <DetailSection title="Wijzigingen in het dashboard">
            {push && push.inserted.length > 0 && (
              <DetailRow label={`Nieuw (${push.inserted.length})`}>
                <ul className="flex flex-col gap-1">
                  {push.inserted.map(r => (
                    <li key={r.id}>
                      <VacatureId id={r.id} />{" "}
                      <span className="text-waves-muted">
                        {r.sheet_row ? `rij ${r.sheet_row}, ` : ""}{r.tab_name}
                      </span>
                      <SheetLink tab={r.tab_name} row={r.sheet_row} />
                      {r.reinserted_from && <span className="text-waves-muted"> (opnieuw ingevoegd, was <VacatureId id={r.reinserted_from} deleted />)</span>}
                    </li>
                  ))}
                </ul>
              </DetailRow>
            )}
            {push && push.updated.length > 0 && (
              <DetailRow label={`Gewijzigd (${push.updated.length})`}>
                <ul className="flex flex-col gap-2">
                  {push.updated.map(u => (
                    <li key={u.id}>
                      <VacatureId id={u.id} />
                      {u.changes && u.changes.length > 0 ? (
                        <ul className="mt-0.5 flex flex-col gap-0.5 pl-4">
                          {u.changes.map(c => (
                            <li key={c.field} className="break-words">
                              <span className="text-waves-muted">{FIELD_LABEL[c.field] ?? c.field}:</span>{" "}
                              <span className="line-through decoration-waves-muted">{c.old || "(leeg)"}</span>
                              {" → "}
                              <strong className="font-semibold text-waves-ink">{c.new || "(leeg)"}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-waves-muted"> {u.fields.map(f => FIELD_LABEL[f] ?? f).join(", ")}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </DetailRow>
            )}
            {del && del.deleted.length > 0 && (
              <DetailRow label={`Verwijderd (${del.deleted.length})`}>
                <IdList ids={del.deleted} deleted />
              </DetailRow>
            )}
            {push && push.unchanged > 0 && (
              <DetailRow label="Ongewijzigd">
                <span className="font-mono tabular-nums">{push.unchanged}</span> rijen
              </DetailRow>
            )}
            {push && push.skipped.length > 0 && (
              <DetailRow label={`Overgeslagen (${push.skipped.length})`}>
                <ul className="flex flex-col gap-1">
                  {push.skipped.map((s, i) => (
                    <li key={i}>
                      <RowRef label={rowLabel(s)} tab={s.tab_name} />
                      <SheetLink tab={s.tab_name} row={s.sheet_row} />
                      {s.id && <> · <VacatureId id={s.id} /></>}:{" "}
                      {SKIP_REASON[s.reason] ?? <IdText text={s.reason} />}
                      {s.reason === "tab name does not match a branch" && (
                        <span className="text-waves-muted">
                          {s.branch_name
                            ? <> — deze vacature hoort bij vestiging <strong className="text-waves-ink">{s.branch_name}</strong></>
                            : s.id ? " — vestiging van deze vacature onbekend" : " — rij zonder ID, dus vestiging onbekend"}
                          {push.branches && push.branches.length > 0 && <>. Tabbladen die wel synchroniseren: {push.branches.join(", ")}</>}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </DetailRow>
            )}
            {del && del.skipped.length > 0 && (
              <DetailRow label={`Niet verwijderd (${del.skipped.length})`}>
                <span className="text-waves-muted">Al verwijderd of van een andere partner: </span>
                <IdList ids={del.skipped} />
              </DetailRow>
            )}
          </DetailSection>
        )}

        {sheet && (
          <DetailSection title="Wijzigingen in de sheet">
            <DetailRow label="Opgehaald / verstuurd">
              <span className="font-mono tabular-nums">{sheet.pulled}</span> vacatures opgehaald ·{" "}
              <span className="font-mono tabular-nums">{sheet.pushed}</span> rijen verstuurd
            </DetailRow>
            {sheetLists.map(([label, ids, deleted]) => ids && ids.length > 0 && (
              <DetailRow key={label} label={`${label} (${ids.length})`}>
                <IdList ids={ids} deleted={deleted} />
              </DetailRow>
            ))}
            {sheet.skipped_rows && sheet.skipped_rows.length > 0 && (
              <DetailRow label={`Niet verstuurd (${sheet.skipped_rows.length})`}>
                <ul className="flex flex-col gap-1">
                  {sheet.skipped_rows.map((s, i) => (
                    <li key={i}>
                      <RowRef label={`Rij ${s.row}`} tab={s.tab_name} />
                      <SheetLink tab={s.tab_name} row={s.row} />:{" "}
                      {s.first_tab && s.id ? (
                        <>
                          dubbele ID <VacatureId id={s.id} /> in kolom T — staat al in tabblad{" "}
                          <strong className="text-waves-ink">{s.first_tab}</strong>, rij {s.first_row}
                          <SheetLink tab={s.first_tab} row={s.first_row} />
                        </>
                      ) : <IdText text={s.reason} />}
                    </li>
                  ))}
                </ul>
              </DetailRow>
            )}
          </DetailSection>
        )}

        {!sheet && (
          <p className="text-sm text-waves-muted">
            Het script heeft (nog) geen eindrapport gestuurd voor deze run — alleen de backendkant is bekend.
          </p>
        )}

        <div className="font-mono text-[12px] text-waves-muted">Run {log.run_id}</div>
      </div>
    </SheetLinks.Provider>
  );
}

// ── Overview: what needs attention ─────────────────────────────────────────────

// SyncToggle switches a partner's sheet sync on or off (admin-only, like this page).
function SyncToggle({ p }: { p: SyncPartnerOverview }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function onChange(enabled: boolean) {
    setBusy(true);
    try {
      await setPartnerSheetsSyncEnabled(p.partner_id, enabled);
      await queryClient.invalidateQueries({ queryKey: ["admin", "sync-logs", "overview"] });
      toast(enabled ? `Synchronisatie voor ${p.partner_name} aangezet` : `Synchronisatie voor ${p.partner_name} uitgezet`, { kind: "success" });
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Opslaan mislukt.", { kind: "error" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="flex items-center gap-2 text-[13px] text-waves-muted">
      <Switch checked={p.sync_enabled} disabled={busy} onCheckedChange={onChange} aria-label={`Synchronisatie ${p.partner_name}`} />
      {p.sync_enabled ? "Aan" : "Uit"}
    </label>
  );
}

function PartnerHealthCard({ p, active, onSelect }: { p: SyncPartnerOverview; active: boolean; onSelect: () => void }) {
  const tone = !p.sync_enabled ? "off"
    : p.stale || p.last_run_status === "error" ? "alert"
    : p.held_deletion_ids.length > 0 || p.last_run_status === "warning" ? "warn" : "ok";
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-card border bg-white p-5 text-left",
        active ? "border-waves-ink" : "border-waves-line",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-base font-bold">{p.partner_name}</div>
          <div className="text-[13px] text-waves-muted">
            Laatste synchronisatie: <span className={cn(p.stale && "font-bold text-status-alert-fg")}>{formatRelative(p.last_pull_at)}</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Pill kind={tone === "off" ? "health-unconfigured" : tone === "alert" ? "health-error" : tone === "warn" ? "hold" : "health-ok"}>
            {!p.sync_enabled ? "Uitgeschakeld" : p.stale ? "Werkt niet" : p.last_run_status ? STATUS_LABEL[p.last_run_status] : "Geen runs"}
          </Pill>
          <SyncToggle p={p} />
        </div>
      </div>

      {!p.sync_enabled && (
        <div className="rounded-nav bg-waves-line/40 px-3 py-2 text-[13px] text-waves-body">
          Synchronisatie staat uit{p.sync_disabled_at && <> sinds {formatRelative(p.sync_disabled_at)}</>}. Het sheet en het dashboard
          wijzigen elkaar niet; wijzigingen in het sheet worden verstuurd zodra je de synchronisatie weer aanzet.
        </div>
      )}

      {p.stale && (
        <div className="rounded-nav bg-status-alert-bg px-3 py-2 text-[13px] text-status-alert-fg">
          {p.last_pull_at
            ? "Al meer dan 35 minuten geen geslaagde synchronisatie. Mogelijk is de sync-sleutel ongeldig of staat de trigger in het sheet uit."
            : "Deze partner heeft nog nooit gesynchroniseerd."}
        </div>
      )}

      {p.held_deletion_ids.length > 0 && (
        <div className="rounded-nav bg-status-warn-bg px-3 py-2 text-[13px] text-status-warn-fg">
          <strong>{p.held_deletion_ids.length} verwijdering(en) wachten op bevestiging</strong>
          {p.held_since && <> sinds {formatRelative(p.held_since)}</>}. Voer in de scripteditor van het sheet{" "}
          <code>VerwijderVacaturesPartnerPortaal</code> (verwijderen) of <code>BehoudVacaturesPartnerPortaal</code> (behouden) uit.
          <div className="mt-1"><IdList ids={p.held_deletion_ids} /></div>
        </div>
      )}

      <div className="flex flex-wrap gap-x-3 font-mono text-[12px] text-waves-muted">
        <span>24 u: {p.runs_24h} runs</span>
        {p.errors_24h > 0 && <span className="text-status-alert-fg">{p.errors_24h} met fouten</span>}
        {p.warnings_24h > 0 && <span className="text-status-warn-fg">{p.warnings_24h} met waarschuwingen</span>}
        <span>laatste push: {formatRelative(p.last_push_at)}</span>
      </div>

      <Button variant="outline" size="sm" className="self-start" onClick={onSelect}>
        {active ? "Alle partners tonen" : "Toon runs van deze partner"}
      </Button>
    </div>
  );
}

function RecurringIssues({ partnerId, partnerName, onFilterVacature }: {
  partnerId?: number;
  partnerName: (id: number) => string;
  onFilterVacature: (id: number) => void;
}) {
  const { data: issues, isLoading } = useSyncIssues(partnerId, ISSUE_DAYS);
  const [expanded, setExpanded] = useState(false);
  if (isLoading || !issues || issues.length === 0) return null;
  const shown = expanded ? issues : issues.slice(0, ISSUES_COLLAPSED);

  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>Terugkerende meldingen · laatste {ISSUE_DAYS} dagen</SectionLabel>
      <div className="flex flex-col divide-y divide-waves-line rounded-card border border-waves-line bg-white">
        {shown.map((issue, i) => {
          const [kind, label] = ISSUE_PILL[issue.kind];
          return (
            <div key={i} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-5 py-3 text-sm">
              <span className="w-14 shrink-0 font-mono text-base font-bold tabular-nums text-waves-ink" title="Aantal gelogde runs">
                {issue.count}×
              </span>
              <Pill kind={kind}>{label}</Pill>
              <div className="min-w-0 flex-1 text-waves-body">
                {!partnerId && <span className="font-bold">{partnerName(issue.partner_id)} · </span>}
                {issue.tab_name && <span className="text-waves-muted">{issue.row ? `Rij ${issue.row} ` : ""}({issue.tab_name}) </span>}
                {issue.id && <><VacatureId id={issue.id} />{" "}</>}
                {SKIP_REASON[issue.message] ?? <IdText text={issue.message} />}
                <div className="text-[12px] text-waves-muted">
                  sinds {formatRelative(issue.first_seen)} · laatst {formatRelative(issue.last_seen)}
                  {issue.id && (
                    <> · <button className="underline underline-offset-2 hover:text-waves-ink" onClick={() => onFilterVacature(issue.id!)}>toon runs</button></>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {issues.length > ISSUES_COLLAPSED && (
        <button className="self-start text-[13px] text-waves-muted underline underline-offset-2 hover:text-waves-ink" onClick={() => setExpanded(e => !e)}>
          {expanded ? "Minder tonen" : `Alle ${issues.length} tonen`}
        </button>
      )}
    </section>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

// dayStart/dayEnd turn an <input type="date"> value into an RFC3339 bound in local time.
const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd = (d: string) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);

const inputClass = "h-10 rounded-pill border border-waves-line bg-white px-4 text-sm text-waves-ink outline-none placeholder:text-waves-muted";

export default function SheetsSync() {
  const [page, setPage] = useState(1);
  const [partner, setPartner] = useState("alle");
  const [status, setStatus] = useState("alle");
  const [trigger, setTrigger] = useState("alle");
  const [vacatureId, setVacatureId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState<SyncSheetsLog | null>(null);

  const { data: partners } = usePartners();
  const { data: overview } = useSyncOverview();
  const partnerNames = useMemo(() => new Map((partners ?? []).map(p => [p.id, p.name])), [partners]);
  const partnerFilter = useMemo(() => [
    { value: "alle", label: "Alle partners" },
    ...(partners ?? []).filter(p => p.has_sync_secret).map(p => ({ value: String(p.id), label: p.name })),
  ], [partners]);

  const partnerId = partner !== "alle" ? Number(partner) : undefined;
  const vacatureFilter = /^\d+$/.test(vacatureId.trim()) ? Number(vacatureId.trim()) : undefined;
  const { data, isLoading } = useSyncSheetsLogs(page, LIMIT, {
    partnerId,
    status: status !== "alle" ? (status as SyncRunStatus) : undefined,
    trigger: trigger !== "alle" ? (trigger as Exclude<SyncRunTrigger, "">) : undefined,
    vacatureId: vacatureFilter,
    createdFrom: dayStart(dateFrom),
    createdTo: dayEnd(dateTo),
  });

  useEffect(() => { setPage(1); }, [partner, status, trigger, vacatureFilter, dateFrom, dateTo]);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const partnerName = (id: number) => partnerNames.get(id) ?? `Partner #${id}`;
  const filtered = partner !== "alle" || status !== "alle" || trigger !== "alle" || !!vacatureId || !!dateFrom || !!dateTo;

  const resetFilters = () => {
    setPartner("alle"); setStatus("alle"); setTrigger("alle"); setVacatureId(""); setDateFrom(""); setDateTo("");
  };

  const columns: DataTableColumn<SyncSheetsLog>[] = [
    {
      key: "when", header: "Tijdstip", width: "190px",
      cell: r => <span className="whitespace-nowrap font-mono text-[13px] tabular-nums text-waves-body">{formatTimestamp(r.started_at ?? r.created_at)}</span>,
    },
    {
      key: "partner", header: "Partner", width: "minmax(160px,1.2fr)",
      cell: r => (
        <div className="min-w-0">
          <div className="truncate font-bold">{partnerName(r.partner_id)}</div>
          <div className="truncate text-[13px] text-waves-muted">{TRIGGER_LABEL[r.trigger]}</div>
        </div>
      ),
    },
    {
      key: "status", header: "Status", width: "140px",
      cell: r => <Pill kind={STATUS_PILL[r.status]}>{STATUS_LABEL[r.status]}</Pill>,
    },
    {
      key: "dashboard", header: "Dashboard", width: "minmax(220px,1.6fr)",
      cell: r => (
        <Summary items={[
          [r.inserted_count, "nieuw"],
          [r.updated_count, "gewijzigd"],
          [r.deleted_count, "verwijderd"],
          [r.skipped_count, "overgeslagen"],
        ]} />
      ),
    },
    {
      key: "sheet", header: "Sheet", width: "minmax(120px,0.8fr)",
      cell: r => <Summary items={[[r.sheet_changed_count, "rijen"]]} />,
    },
    {
      key: "errors", header: "Fouten", width: "90px",
      cell: r => r.error_count > 0
        ? <span className="font-mono text-sm font-bold tabular-nums text-status-alert-fg">{r.error_count}</span>
        : <span className="text-waves-muted">—</span>,
    },
  ];

  return (
    <Layout
      title="Sheets-synchronisatie"
      subtitle={`Wat elke synchronisatie met Google Sheets heeft gewijzigd · ${total.toLocaleString("nl-NL")} runs`}
    >
      <VacatureRefs.Provider value={data?.vacatures ?? {}}>
        <div className="mx-auto flex max-w-[1240px] flex-col gap-6">
          {overview && overview.length > 0 && (
            <section className="flex flex-col gap-2">
              <SectionLabel>Status per partner</SectionLabel>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {overview.map(p => (
                  <PartnerHealthCard
                    key={p.partner_id}
                    p={p}
                    active={partnerId === p.partner_id}
                    onSelect={() => setPartner(partnerId === p.partner_id ? "alle" : String(p.partner_id))}
                  />
                ))}
              </div>
            </section>
          )}

          <RecurringIssues partnerId={partnerId} partnerName={partnerName} onFilterVacature={id => setVacatureId(String(id))} />

          <section className="flex flex-col gap-3">
            <SectionLabel>Runs</SectionLabel>
            <div className="flex flex-wrap items-center gap-3">
              <FilterDropdown aria-label="Partner" value={partner} onChange={setPartner} options={partnerFilter} />
              <FilterDropdown aria-label="Status" value={status} onChange={setStatus} options={STATUS_FILTER} />
              <FilterDropdown aria-label="Aanleiding" value={trigger} onChange={setTrigger} options={TRIGGER_FILTER} />
              <div className="flex h-10 w-[170px] items-center gap-2 rounded-pill border border-waves-line bg-white px-4">
                <Search size={15} className="shrink-0 text-waves-muted" />
                <input
                  value={vacatureId}
                  onChange={e => setVacatureId(e.target.value.replace(/[^\d]/g, ""))}
                  inputMode="numeric"
                  placeholder="Vacature-ID"
                  aria-label="Alleen runs met deze vacature"
                  className="min-w-0 flex-1 bg-transparent text-sm text-waves-ink outline-none placeholder:text-waves-muted"
                />
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} aria-label="Vanaf" className={inputClass} />
              <span className="text-sm text-waves-muted">t/m</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} aria-label="Tot en met" className={inputClass} />
              {filtered && (
                <Button variant="outline" size="sm" onClick={resetFilters}><X /> Filters wissen</Button>
              )}
              <div className="ml-auto font-mono text-[13px] text-waves-muted">{rows.length} rijen</div>
            </div>

            <DataTable
              columns={columns}
              rows={rows}
              rowKey={r => r.id}
              minWidth={980}
              onRowClick={setSelected}
              empty={isLoading ? "Laden…" : filtered
                ? "Geen runs gevonden met deze filters."
                : "Nog geen synchronisaties gelogd. Alleen runs met wijzigingen of fouten worden bewaard."}
            />

            <div className="flex items-center justify-between">
              <span className="font-mono text-[13px] text-waves-muted">
                {total === 0 ? 0 : (page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} van {total.toLocaleString("nl-NL")}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft /> Vorige
                </Button>
                <span className="px-2 font-mono text-[13px] text-waves-body">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  Volgende <ChevronRight />
                </Button>
              </div>
            </div>
          </section>
        </div>

        <Dialog open={selected !== null} onOpenChange={open => { if (!open) setSelected(null); }}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
            {selected && <SyncRunDetail log={selected} partnerName={partnerName(selected.partner_id)} />}
          </DialogContent>
        </Dialog>
      </VacatureRefs.Provider>
    </Layout>
  );
}

// Summary renders "3 nieuw · 1 gewijzigd", leaving out zero counts.
function Summary({ items }: { items: [number, string][] }) {
  const parts = items.filter(([n]) => n > 0);
  if (parts.length === 0) return <span className="text-waves-muted">—</span>;
  return (
    <span className="flex flex-wrap gap-x-2 text-sm text-waves-body">
      {parts.map(([n, label], i) => (
        <span key={label}>
          {i > 0 && <span className="mr-2 text-waves-muted">·</span>}
          <span className="font-mono tabular-nums">{n}</span> {label}
        </span>
      ))}
    </span>
  );
}
